import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import useAuthStore from '../stores/authStore';
import useLang from '../hooks/useLang';

const fmt = (v) => new Intl.NumberFormat('sw-TZ').format(v || 0);

export default function Fines() {
  const { T } = useLang();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const [searchParams] = useSearchParams();

  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState(searchParams.get('group_id') || user?.group_id || '');
  const [fines, setFines] = useState([]);
  const [members, setMembers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [autoMsg, setAutoMsg] = useState('');
  const [autoLoading, setAutoLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ member_id: '', period: new Date().toISOString().slice(0, 7), amount: '', reason: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.role === 'superadmin') api.get('/groups').then(r => setGroups(r.data));
  }, [user]);

  useEffect(() => {
    if (groupId) api.get(`/members?group_id=${groupId}`).then(r => setMembers(r.data));
  }, [groupId]);

  const load = () => {
    if (!groupId) return;
    setLoading(true);
    const params = new URLSearchParams({ group_id: groupId });
    if (statusFilter) params.set('status', statusFilter);
    api.get(`/fines?${params}`).then(r => setFines(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [groupId, statusFilter]);

  const handleAutoCheck = async () => {
    if (!groupId) return;
    const period = prompt('Angalia faini za mwezi gani? (mfano: 2025-06)', new Date().toISOString().slice(0, 7));
    if (!period) return;
    setAutoLoading(true);
    setAutoMsg('');
    try {
      const { data } = await api.post('/fines/auto-check', { group_id: groupId, period });
      setAutoMsg(data.message);
      load();
    } catch (e) {
      setAutoMsg(e.response?.data?.error || 'Imeshindwa');
    } finally {
      setAutoLoading(false);
    }
  };

  const handleAction = async (id, action) => {
    await api.post(`/fines/${id}/${action}`);
    load();
  };

  const handleDelete = async (id) => {
    if (!confirm('Futa faini hii?')) return;
    await api.delete(`/fines/${id}`);
    load();
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await api.post('/fines', { ...form, group_id: groupId });
      setShowModal(false);
      setForm({ member_id: '', period: new Date().toISOString().slice(0, 7), amount: '', reason: '' });
      load();
    } catch (err) {
      setError(err.response?.data?.error || T.fines.errCreate);
    } finally {
      setSaving(false);
    }
  };

  const pending = fines.filter(f => f.status === 'pending');
  const totalPending = pending.reduce((s, f) => s + parseFloat(f.amount || 0), 0);

  const statusBadge = (s) => {
    const map = { pending: 'bg-red-100 text-red-700', paid: 'bg-green-100 text-green-700', waived: 'bg-gray-100 text-gray-500' };
    const label = { pending: T.fines.pending, paid: T.fines.paid, waived: T.fines.waived };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[s]}`}>{label[s]}</span>;
  };

  return (
    <div className="space-y-5">
      <div className="overflow-hidden rounded-[30px] border border-white/70 bg-[linear-gradient(135deg,rgba(127,29,29,0.96),rgba(15,23,42,0.94))] px-5 py-6 text-white shadow-[0_24px_60px_rgba(15,23,42,0.18)] sm:px-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-red-100/80">{T.fines.badgeLabel}</p>
            <h1 className="mt-2 text-2xl font-bold sm:text-3xl">{T.fines.title}</h1>
            <p className="mt-2 text-sm text-red-50/80">
              {T.fines.subtitle}
            </p>
          </div>
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[24px] bg-white/12 text-3xl shadow-inner shadow-white/10">
            ⚠️
          </div>
        </div>

        {isAdmin && (
          <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            <button onClick={handleAutoCheck} disabled={!groupId || autoLoading}
              className="rounded-2xl bg-amber-400 px-4 py-3 text-sm font-medium text-slate-900 shadow-[0_12px_30px_rgba(251,191,36,0.25)] transition hover:bg-amber-300 disabled:opacity-50">
              {autoLoading ? T.fines.checking : `🔍 ${T.fines.checkFines}`}
            </button>
            {groupId && (
              <button onClick={() => setShowModal(true)}
                className="rounded-2xl bg-white/12 px-4 py-3 text-sm font-medium text-white transition hover:bg-white/18">
                {T.fines.addFine}
              </button>
            )}
          </div>
        )}
      </div>

      {autoMsg && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-4 py-3 text-sm">
          {autoMsg}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        {user?.role === 'superadmin' && (
          <select value={groupId} onChange={e => setGroupId(e.target.value)}
            className="rounded-2xl border border-gray-300 bg-white/90 px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
            <option value="">{T.fines.chooseGroup}</option>
            {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        )}
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="rounded-2xl border border-gray-300 bg-white/90 px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
          <option value="">{T.fines.allStatus}</option>
          <option value="pending">{T.fines.pending}</option>
          <option value="paid">{T.fines.paid}</option>
          <option value="waived">{T.fines.waived}</option>
        </select>
      </div>

      {/* Summary */}
      {!loading && pending.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-[24px] border border-red-200 bg-[linear-gradient(135deg,rgba(254,242,242,0.96),rgba(254,226,226,0.92))] p-4">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-red-500">{T.fines.pendingCount}</div>
            <div className="mt-2 text-2xl font-bold text-red-700">{pending.length}</div>
            <div className="text-xs text-red-500">{T.fines.pendingCountLabel}</div>
          </div>
          <div className="rounded-[24px] border border-amber-200 bg-[linear-gradient(135deg,rgba(255,251,235,0.96),rgba(254,243,199,0.92))] p-4">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-600">{T.fines.pendingTotal}</div>
            <div className="mt-2 text-2xl font-bold text-amber-700">{fmt(totalPending)}</div>
            <div className="text-xs text-amber-600">{T.fines.pendingTotalLabel}</div>
          </div>
        </div>
      )}

      {!groupId ? (
        <div className="text-center py-16 text-gray-400">{T.fines.chooseFirst}</div>
      ) : loading ? (
        <div className="text-center py-16 text-gray-500">{T.common.loading}</div>
      ) : (
        <div className="space-y-3">
          <div className="space-y-3 md:hidden">
            {fines.map((f) => (
              <div key={f.id} className="rounded-[24px] border border-white/75 bg-white/88 p-4 shadow-[0_14px_35px_rgba(15,23,42,0.08)]">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-slate-800">{f.member_name}</p>
                    <p className="mt-1 text-xs text-slate-500">{f.member_phone}</p>
                  </div>
                  <span className="rounded-full bg-red-50 px-3 py-1 text-sm font-bold text-red-600">{fmt(f.amount)}</span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <MobileMeta label="Mwezi" value={f.period} mono />
                  <MobileMeta label="Aina" value={f.type === 'auto' ? 'Auto' : 'Manual'} />
                </div>
                <div className="mt-3 rounded-2xl bg-slate-50 px-3 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Sababu</p>
                  <p className="mt-1 text-sm text-slate-600">{f.reason || '—'}</p>
                </div>
                <div className="mt-3 flex items-center justify-between gap-3">
                  <div>{statusBadge(f.status)}</div>
                  {isAdmin && (
                    <div className="flex gap-2">
                      {f.status === 'pending' && (
                        <>
                          <button onClick={() => handleAction(f.id, 'pay')} className="rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-700">{T.fines.pay}</button>
                          <button onClick={() => handleAction(f.id, 'waive')} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">{T.fines.waive}</button>
                        </>
                      )}
                      <button onClick={() => handleDelete(f.id)} className="rounded-full bg-rose-50 px-3 py-1 text-xs font-medium text-rose-600">{T.fines.delete}</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {!fines.length && <div className="text-center py-8 text-gray-400">{T.fines.noFines}</div>}
          </div>

          <div className="hidden overflow-x-auto rounded-xl border border-gray-200 bg-white md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-gray-500 text-xs uppercase">
                  <th className="text-left px-4 py-3">{T.fines.member}</th>
                  <th className="text-left px-4 py-3">{T.fines.period}</th>
                  <th className="text-left px-4 py-3">{T.fines.reason}</th>
                  <th className="text-right px-4 py-3">{T.fines.amount}</th>
                  <th className="text-left px-4 py-3">{T.fines.type}</th>
                  <th className="text-left px-4 py-3">{T.fines.statusLabel}</th>
                  <th className="px-4 py-3">{T.fines.actions}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {fines.map(f => (
                  <tr key={f.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">
                      {f.member_name}
                      <div className="text-xs text-gray-400">{f.member_phone}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700 font-mono">{f.period}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{f.reason}</td>
                    <td className="px-4 py-3 text-right font-semibold text-red-600">{fmt(f.amount)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${f.type === 'auto' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}>
                        {f.type === 'auto' ? 'Auto' : 'Manual'}
                      </span>
                    </td>
                    <td className="px-4 py-3">{statusBadge(f.status)}</td>
                    <td className="px-4 py-3">
                      {isAdmin && (
                        <div className="flex gap-2">
                          {f.status === 'pending' && (
                            <>
                              <button onClick={() => handleAction(f.id, 'pay')}
                                className="text-green-600 hover:text-green-800 text-xs font-medium">{T.fines.pay}</button>
                              <button onClick={() => handleAction(f.id, 'waive')}
                                className="text-gray-500 hover:text-gray-700 text-xs">{T.fines.waive}</button>
                            </>
                          )}
                          <button onClick={() => handleDelete(f.id)}
                            className="text-red-400 hover:text-red-600 text-xs">{T.fines.delete}</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {!fines.length && (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-gray-400">{T.fines.noFines}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Manual fine modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-5">{T.fines.manualFine}</h2>
            {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">{error}</div>}
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{T.fines.member} *</label>
                <select required value={form.member_id} onChange={e => setForm({ ...form, member_id: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="">{T.fines.chooseSelect}</option>
                  {members.map(m => <option key={m.id} value={m.id}>{m.name} ({m.phone})</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{T.fines.period} *</label>
                  <input required type="month" value={form.period}
                    onChange={e => setForm({ ...form, period: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{T.fines.amount} *</label>
                  <input required type="number" min="1" value={form.amount}
                    onChange={e => setForm({ ...form, amount: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{T.fines.reason}</label>
                <input type="text" value={form.reason}
                  onChange={e => setForm({ ...form, reason: e.target.value })}
                  placeholder={T.fines.reasonPlaceholder}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowModal(false); setError(''); }}
                  className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50">{T.common.cancel}</button>
                <button type="submit" disabled={saving}
                  className="flex-1 bg-red-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-60">
                  {saving ? T.fines.saving : T.fines.submit}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function MobileMeta({ label, value, mono = false }) {
  return (
    <div className="rounded-2xl bg-slate-50 px-3 py-2">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
      <p className={`mt-1 text-sm font-medium text-slate-700 ${mono ? 'font-mono' : ''}`}>{value || '—'}</p>
    </div>
  );
}
